import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import { CONDITIONS } from '../data/conditionProfiles';

const C = {
  bg: '#0D0D12', surface: '#16161F', card: '#1C1C28', border: '#2A2A3A',
  accent: '#E8A2B8', accentSoft: '#C97A9A', teal: '#7ECECA',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5', purple: '#B39DDB',
};

const GOALS = [
  { id: 'track_cycle', label: 'Track my cycle', icon: '◌' },
  { id: 'understand_symptoms', label: 'Understand my symptoms', icon: '◈' },
  { id: 'skin_health', label: 'Improve my skin', icon: '◇' },
  { id: 'gp_appointments', label: 'Prepare for GP appointments', icon: '⊕' },
  { id: 'research', label: 'Understand the research', icon: '◎' },
  { id: 'medications', label: 'Manage medications', icon: '⊞' },
];

const CYCLE_PHASES = [
  { id: 'menstruating', label: 'Menstruating', sub: 'currently on my period' },
  { id: 'post_period', label: 'Just finished', sub: 'period ended recently' },
  { id: 'mid_cycle', label: 'Mid-cycle', sub: 'roughly halfway through' },
  { id: 'pre_period', label: 'Approaching my period', sub: 'due in the next week or so' },
  { id: 'unsure', label: 'Not sure', sub: "I'll track as I go" },
];

const DETAIL_OPTIONS = [
  { id: 'quick', label: 'Quick', sub: 'Severity only — one tap per symptom', icon: '⚡' },
  { id: 'standard', label: 'Standard', sub: 'Severity + location / type where relevant', icon: '◎' },
  { id: 'full', label: 'Full detail', sub: 'All sub-attributes shown — great for clinical prep', icon: '◈' },
];

const LOG_TIME_OPTIONS = [
  { id: 'morning', label: 'Morning', sub: 'Copy-forward from yesterday' },
  { id: 'evening', label: 'Evening', sub: 'End-of-day reflection' },
  { id: 'as_needed', label: 'As symptoms happen', sub: 'No fixed time' },
];

function ProgressDots({ current, total }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 28 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ width: i === current ? 20 : 6, height: 6, borderRadius: 3, background: i === current ? C.accent : C.border, transition: 'all 0.3s ease' }} />
      ))}
    </div>
  );
}

function Screen({ children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '75vh', padding: '24px 4px', animation: 'fadeIn 0.3s ease' }}>
      {children}
    </div>
  );
}

function PrimaryButton({ onClick, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: disabled ? C.border : C.accent, color: disabled ? C.muted : '#0D0D12', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', width: '100%', transition: 'all 0.2s ease', fontFamily: "'Inter', sans-serif" }}>
      {children}
    </button>
  );
}

function GhostButton({ onClick, children }) {
  return (
    <button onClick={onClick} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', width: '100%', marginTop: 12, fontFamily: 'monospace', letterSpacing: '0.06em' }}>
      {children}
    </button>
  );
}

function StepHeader({ step, total, title, sub }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{ fontSize: 11, color: C.accent, fontFamily: 'monospace', letterSpacing: '0.1em', margin: '0 0 8px' }}>STEP {step} OF {total}</p>
      <h2 style={{ fontSize: 21, color: C.text, fontWeight: 700, margin: '0 0 8px', lineHeight: 1.3 }}>{title}</h2>
      {sub && <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.6 }}>{sub}</p>}
    </div>
  );
}

function WelcomeScreen({ onNext }) {
  return (
    <Screen>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: `radial-gradient(circle at 35% 35%, ${C.accentSoft}40, ${C.accent}15)`, border: `1px solid ${C.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <span style={{ fontSize: 28, color: C.accent }}>◉</span>
          </div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 40, color: C.accent, lineHeight: 1 }}>Sylva</div>
        </div>
        <div style={{ marginBottom: 36 }}>
          <p style={{ fontSize: 20, color: C.text, fontWeight: 600, lineHeight: 1.4, margin: '0 0 16px' }}>Your body, understood.</p>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, margin: 0 }}>Sylva is built on evidence, not assumptions. Every insight is grounded in peer-reviewed research.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 40 }}>
          {[{ icon: '◈', label: 'Evidence-based', desc: 'Grounded in real research' }, { icon: '◇', label: 'Misinformation-free', desc: "We flag what isn't proven" }, { icon: '◌', label: 'Yours alone', desc: 'Your data stays private' }].map(p => (
            <div key={p.label} style={{ background: C.card, borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, border: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 18, color: C.accent, width: 24, textAlign: 'center' }}>{p.icon}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <PrimaryButton onClick={onNext}>Let's get started</PrimaryButton>
    </Screen>
  );
}

function CycleScreen({ value, onChange, onNext, onSkip }) {
  return (
    <Screen>
      <ProgressDots current={0} total={5} />
      <StepHeader step={1} total={5} title="Where are you in your cycle right now?" sub="Helps Sylva show the most relevant insights from day one." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        {CYCLE_PHASES.map(phase => (
          <button key={phase.id} onClick={() => onChange(phase.id)} style={{ background: value === phase.id ? `${C.accent}18` : C.card, border: `1px solid ${value === phase.id ? C.accent : C.border}`, borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}>
            <div>
              <div style={{ fontSize: 14, color: C.text, fontWeight: 600 }}>{phase.label}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{phase.sub}</div>
            </div>
            <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${value === phase.id ? C.accent : C.border}`, background: value === phase.id ? C.accent : 'transparent', flexShrink: 0 }} />
          </button>
        ))}
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
        <GhostButton onClick={onSkip}>SKIP THIS STEP</GhostButton>
      </div>
    </Screen>
  );
}

function GoalsScreen({ values, onChange, onNext, onSkip }) {
  const toggle = (id) => onChange(values.includes(id) ? values.filter(v => v !== id) : [...values, id]);
  return (
    <Screen>
      <ProgressDots current={1} total={5} />
      <StepHeader step={2} total={5} title="What do you want to get out of Sylva?" sub="Select as many as you like. We'll tailor what you see." />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, flex: 1 }}>
        {GOALS.map(goal => {
          const selected = values.includes(goal.id);
          return (
            <button key={goal.id} onClick={() => toggle(goal.id)} style={{ background: selected ? `${C.accent}18` : C.card, border: `1px solid ${selected ? C.accent : C.border}`, borderRadius: 14, padding: '16px 12px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'all 0.2s ease' }}>
              <span style={{ fontSize: 22, color: selected ? C.accent : C.muted }}>{goal.icon}</span>
              <span style={{ fontSize: 12, color: selected ? C.text : C.muted, fontWeight: selected ? 600 : 400, lineHeight: 1.3 }}>{goal.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton onClick={onNext} disabled={values.length === 0}>Continue</PrimaryButton>
        <GhostButton onClick={onSkip}>SKIP THIS STEP</GhostButton>
      </div>
    </Screen>
  );
}

function ConditionsScreen({ values, onChange, onNext, onSkip }) {
  const toggle = (id) => onChange(values.includes(id) ? values.filter(v => v !== id) : [...values, id]);
  const noneSelected = values.includes('none');
  return (
    <Screen>
      <ProgressDots current={2} total={5} />
      <StepHeader step={3} total={5} title="Do you have any of these conditions?" sub="Optional — lets Sylva surface the most relevant symptom detail automatically. You can update this any time." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {CONDITIONS.map(c => {
          const selected = values.includes(c.id);
          return (
            <button key={c.id} onClick={() => { if (!noneSelected) toggle(c.id); }} style={{ background: selected ? `${C.teal}18` : C.card, border: `1px solid ${selected ? C.teal : C.border}`, borderRadius: 12, padding: '12px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.15s', opacity: noneSelected && !selected ? 0.4 : 1, width: '100%' }}>
              <span style={{ fontSize: 13, color: selected ? C.text : C.muted, fontWeight: selected ? 600 : 400 }}>{c.label}</span>
              <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${selected ? C.teal : C.border}`, background: selected ? C.teal : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {selected && <span style={{ color: '#0D0D12', fontSize: 10, fontWeight: 700 }}>✓</span>}
              </div>
            </button>
          );
        })}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4 }}>
          <button onClick={() => onChange(noneSelected ? [] : ['none'])} style={{ background: noneSelected ? `${C.muted}18` : C.card, border: `1px solid ${noneSelected ? C.muted : C.border}`, borderRadius: 12, padding: '12px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ fontSize: 13, color: C.muted }}>None / prefer not to say</span>
            <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${noneSelected ? C.muted : C.border}`, background: noneSelected ? C.muted : 'transparent', flexShrink: 0 }} />
          </button>
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton onClick={onNext} disabled={values.length === 0}>Continue</PrimaryButton>
        <GhostButton onClick={onSkip}>SKIP THIS STEP</GhostButton>
      </div>
    </Screen>
  );
}

function DetailScreen({ value, onChange, logTime, onLogTimeChange, onNext, onSkip }) {
  return (
    <Screen>
      <ProgressDots current={3} total={5} />
      <StepHeader step={4} total={5} title="How detailed do you want your logs?" sub="You can always switch this per-session using the Quick / Full toggle." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {DETAIL_OPTIONS.map(opt => (
          <button key={opt.id} onClick={() => onChange(opt.id)} style={{ background: value === opt.id ? `${C.accent}18` : C.card, border: `1px solid ${value === opt.id ? C.accent : C.border}`, borderRadius: 14, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s' }}>
            <span style={{ fontSize: 20, color: value === opt.id ? C.accent : C.muted }}>{opt.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: C.text, fontWeight: value === opt.id ? 700 : 400 }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{opt.sub}</div>
            </div>
            <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${value === opt.id ? C.accent : C.border}`, background: value === opt.id ? C.accent : 'transparent', flexShrink: 0 }} />
          </button>
        ))}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 10 }}>WHEN DO YOU PREFER TO LOG?</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {LOG_TIME_OPTIONS.map(opt => (
            <button key={opt.id} onClick={() => onLogTimeChange(opt.id)} style={{ flex: 1, background: logTime === opt.id ? `${C.accent}18` : C.card, border: `1px solid ${logTime === opt.id ? C.accent : C.border}`, borderRadius: 12, padding: '10px 8px', cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: logTime === opt.id ? C.accent : C.text, fontWeight: logTime === opt.id ? 700 : 400 }}>{opt.label}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.3 }}>{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 16 }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
        <GhostButton onClick={onSkip}>SKIP THIS STEP</GhostButton>
      </div>
    </Screen>
  );
}

function ReadyScreen({ goals, conditions, detailPref, onEnter, loading }) {
  return (
    <Screen>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: `radial-gradient(circle at 35% 35%, ${C.green}40, ${C.teal}15)`, border: `1px solid ${C.green}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <span style={{ fontSize: 28, color: C.green }}>✓</span>
        </div>
        <h2 style={{ fontSize: 24, color: C.text, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.3 }}>Sylva is ready for you</h2>
        <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, margin: '0 0 24px' }}>We've personalised your experience. You can update any of this in settings.</p>
        <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.border}`, textAlign: 'left' }}>
          {goals.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', marginBottom: 6 }}>YOUR GOALS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {goals.map(g => { const goal = GOALS.find(x => x.id === g); return goal ? <span key={g} style={{ background: `${C.accent}20`, border: `1px solid ${C.accent}40`, borderRadius: 8, padding: '3px 10px', fontSize: 11, color: C.accent }}>{goal.label}</span> : null; })}
              </div>
            </div>
          )}
          {conditions.filter(c => c !== 'none').length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', marginBottom: 6 }}>CONDITIONS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {conditions.filter(c => c !== 'none').map(id => { const cond = CONDITIONS.find(x => x.id === id); return cond ? <span key={id} style={{ background: `${C.teal}20`, border: `1px solid ${C.teal}40`, borderRadius: 8, padding: '3px 10px', fontSize: 11, color: C.teal }}>{cond.label}</span> : null; })}
              </div>
            </div>
          )}
          {detailPref && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', marginBottom: 6 }}>LOG DETAIL</div>
              <span style={{ background: `${C.purple}20`, border: `1px solid ${C.purple}40`, borderRadius: 8, padding: '3px 10px', fontSize: 11, color: C.purple }}>
                {DETAIL_OPTIONS.find(d => d.id === detailPref)?.label || detailPref}
              </span>
            </div>
          )}
        </div>
      </div>
      <PrimaryButton onClick={onEnter} disabled={loading}>{loading ? 'Setting up…' : 'Enter Sylva'}</PrimaryButton>
    </Screen>
  );
}

export default function Onboarding({ setUser }) {
  const [step, setStep] = useState(0);
  const [cyclePhase, setCyclePhase] = useState('');
  const [goals, setGoals] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [detailPref, setDetailPref] = useState('quick');
  const [logTimePref, setLogTimePref] = useState('evening');
  const [saving, setSaving] = useState(false);

  const next = () => setStep(s => s + 1);

  const saveAndFinish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.post(
        `${API_URL}/api/auth/onboarding`,
        { cycle_phase: cyclePhase, goals, challenge: null, conditions: conditions.filter(c => c !== 'none'), detail_preference: detailPref, log_time_preference: logTimePref },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(res.data);
    } catch (err) {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const me = await axios.get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          setUser({ ...me.data, onboarding_complete: true });
        } catch {
          setUser(u => ({ ...u, onboarding_complete: true }));
        }
      }
    }
    setSaving(false);
  };

  return (
    <div style={{ background: '#08080F', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '20px 16px', fontFamily: "'Inter', sans-serif" }}>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{ width: '100%', maxWidth: 390, background: C.bg, borderRadius: 36, overflow: 'hidden', boxShadow: '0 40px 120px rgba(0,0,0,0.7), 0 0 0 1px #2A2A3A', minHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, padding: '24px 20px 32px', display: 'flex', flexDirection: 'column' }}>
          {step === 0 && <WelcomeScreen onNext={next} />}
          {step === 1 && <CycleScreen value={cyclePhase} onChange={setCyclePhase} onNext={next} onSkip={next} />}
          {step === 2 && <GoalsScreen values={goals} onChange={setGoals} onNext={next} onSkip={next} />}
          {step === 3 && <ConditionsScreen values={conditions} onChange={setConditions} onNext={next} onSkip={next} />}
          {step === 4 && <DetailScreen value={detailPref} onChange={setDetailPref} logTime={logTimePref} onLogTimeChange={setLogTimePref} onNext={next} onSkip={next} />}
          {step === 5 && <ReadyScreen goals={goals} conditions={conditions} detailPref={detailPref} onEnter={saveAndFinish} loading={saving} />}
        </div>
      </div>
    </div>
  );
}
